import { Card, Empty, Skeleton, Tabs } from 'antd';
import { TabsProps } from 'antd/lib/tabs';
import React, { useEffect, useState } from 'react';

import { ITabSelector, useTabSelectorWithState } from '../../hooks/useTabSelector';

export interface ITab {
  key: string;
  title: string;
  loading: boolean;
  empty: boolean;
  onTouchedTab?: () => any;
  content: () => React.ReactNode;
}

interface ITabsManagerProps {
  tabs: (ITab | null)[];
  tabsProps?: TabsProps;
  cardBodyStyle?: React.CSSProperties;
  tabSelector?: ITabSelector<any>;
}

export const TabsManager: React.FC<ITabsManagerProps> = (props) => {
  const { tabs, tabsProps, cardBodyStyle } = props;

  const [tabsReady, setTabsReady] = useState(false);
  const [touchedTabs, setTouchedTabs] = useState<{ [key: string]: boolean }>({});

  const tabsHidden = tabs.every(t => !t);
  const tabsLoading = tabs.some(t => t && t.loading && !t.empty);
  const tabsEmpty = !tabsLoading && tabs.every(t => t && t.empty);

  const { defaultActiveKey, activeKey, selectTab } = props.tabSelector || useTabSelectorWithState({
    defaultActiveKey: !tabsLoading
      ? tabs.find(t => t && !t.empty)?.key || '-1' // Attempt to select first available tab
      : '-1', // No available tabs
  });

  useEffect(() => {
    if (!tabsReady && !tabsLoading) {
      setTabsReady(true); // Avoid hiding/showing all tabs when using pagination
      setTouchedTabs({ ...touchedTabs, [defaultActiveKey]: true });

      if (activeKey === '-1') {
        selectTab(defaultActiveKey); // Select default after loading tabs are ready
      }

      const defaultActiveTab = tabs.find(t => t?.key === defaultActiveKey);

      if (defaultActiveTab) {
        defaultActiveTab.onTouchedTab?.(); // Ensure the default tab loads its content
      }
    }
  }, [tabsReady, tabsLoading, defaultActiveKey]);

  useEffect(() => {
    // Reset state if tabs start reloading
    if (tabsReady && tabsLoading) {
      setTabsReady(false);
      setTouchedTabs({});
      selectTab(defaultActiveKey);
    }
  }, [tabsLoading]);

  if (tabsHidden) {
    return null;
  }

  if (!tabsReady) {
    return (
      <Card bordered bodyStyle={cardBodyStyle}>
        <Skeleton loading active />
      </Card>
    );
  }

  return (
    <Card bordered={false} bodyStyle={cardBodyStyle}>
      <Tabs
        animated={false}
        defaultActiveKey={defaultActiveKey}
        activeKey={activeKey}
        onChange={(nextActiveKey) => {
          selectTab(nextActiveKey);
          setTouchedTabs({ ...touchedTabs, [nextActiveKey]: true });

          const nextActiveTab = tabs.find(t => t && t.key === nextActiveKey);

          if (nextActiveTab) {
            nextActiveTab?.onTouchedTab?.();
          }
        }}
        {...tabsProps}
      >
        {tabs.map((tab) => {
          if (!tab) {
            return null;
          }

          const { key, empty, title, content } = tab;

          return (
            <Tabs.TabPane
              key={key}
              tab={title}
              disabled={empty}
            >
              {!empty ? content() : null}
            </Tabs.TabPane>
          );
        })}
      </Tabs>
      {tabsEmpty && <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />}
    </Card>
  );
}

export default TabsManager;
